import React from 'react';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './ArtifactFrame.module.css';
import type { ArtifactKind, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ArtifactFrameProps {
  /**
   * Neither `document` nor `website` reaches here — both replace the mat instead of floating on it,
   * and both render through `DocumentSurface`.
   */
  kind: Exclude<ArtifactKind, 'document' | 'website'>;
  /** The artifact's own coordinate space. `children` are authored at this size, in these units. */
  naturalWidth: number;
  naturalHeight: number;
  /** On-screen scale, from `useArtifactFit`. */
  scale: number;
  /** The frame's on-screen box, from `useArtifactFit`. */
  width: number;
  height: number;
  /** The artifact itself, authored in natural coordinates. */
  children?: React.ReactNode;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The artifact, floating and centred: a white card with a 10px radius and one deep shadow.
 *
 * ## Why this is two boxes and not a `transform: scale()` on one
 *
 * The specs measured the same frame at 0.65x, 1.00x and 1.10x and found that everything inside it
 * moves — padding, type size, rule widths — while the 10px radius and the `0 26px 70px` shadow do
 * not. A single scaled card cannot do that: `transform` takes the border-radius and the box-shadow
 * with it, so the corners round harder as you zoom in and the shadow balloons, and the artifact
 * stops reading as one object sitting on a surface and starts reading as a photograph of one being
 * zoomed.
 *
 * So the scale is applied one level DOWN. `.frame` is drawn in screen pixels at the fitted size and
 * owns the radius, the shadow, the ground and the clip; `.canvas` inside it is the artifact's own
 * fixed coordinate space — 1280 x 720 for a slide, whatever the file is for an image — and carries
 * the only `transform` in the component. Chrome stays chrome, content scales, and the two never
 * touch. It also means everything a caller writes inside this frame is authored once, at natural
 * size, and every number the pane reports about the artifact (`1920 x 1080`, a font size of 64) is
 * in those same units rather than in screen pixels.
 *
 * `transform-origin: 0 0` is what makes the arithmetic trivial: the canvas grows right and down
 * from the frame's top-left corner, so `natural * scale` is exactly the frame's size and there is
 * no half-a-scaled-pixel offset to correct for.
 */
export function ArtifactFrame({
  kind,
  naturalWidth,
  naturalHeight,
  scale,
  width,
  height,
  children,
  theme: themeProp,
  className,
}: ArtifactFrameProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      className={cn(themes[theme], styles.frame, styles[kind], className)}
      style={
        {
          '--hv15-art-w': `${width}px`,
          '--hv15-art-h': `${height}px`,
        } as React.CSSProperties
      }
    >
      <div className={styles.viewport}>
        <div
          className={styles.canvas}
          // What the stage's page shortcuts call the stage: a key pressed with focus here is the artifact's.
          data-artifact-canvas=""
          style={
            {
              '--hv15-art-nat-w': `${naturalWidth}px`,
              '--hv15-art-nat-h': `${naturalHeight}px`,
              '--hv15-art-scale': `${scale}`,
            } as React.CSSProperties
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}
